import { PeriodTransaction, KPIData, LineChartDataset } from './types';

// Fixed color palette for consistent store colors across renders
const STORE_COLORS = [
  { bg: 'rgba(25, 118, 210, 0.2)', border: 'rgba(25, 118, 210, 1)' },   // blue
  { bg: 'rgba(56, 142, 60, 0.2)', border: 'rgba(56, 142, 60, 1)' },     // green
  { bg: 'rgba(245, 124, 0, 0.2)', border: 'rgba(245, 124, 0, 1)' },     // orange
  { bg: 'rgba(211, 47, 47, 0.2)', border: 'rgba(211, 47, 47, 1)' },     // red
  { bg: 'rgba(123, 31, 162, 0.2)', border: 'rgba(123, 31, 162, 1)' },   // purple
  { bg: 'rgba(0, 151, 167, 0.2)', border: 'rgba(0, 151, 167, 1)' },     // teal
  { bg: 'rgba(255, 179, 0, 0.2)', border: 'rgba(255, 179, 0, 1)' },     // amber
  { bg: 'rgba(69, 90, 100, 0.2)', border: 'rgba(69, 90, 100, 1)' },     // blue-grey
  { bg: 'rgba(233, 30, 99, 0.2)', border: 'rgba(233, 30, 99, 1)' },     // pink
  { bg: 'rgba(76, 175, 80, 0.2)', border: 'rgba(76, 175, 80, 1)' },     // light green
];

export function getStoreColor(index: number) {
  return STORE_COLORS[index % STORE_COLORS.length];
}

function resolveEntryMonth(entry: PeriodTransaction): number | null {
  if (entry.Month != null) {
    return entry.Month;
  }
  if (entry.Week != null) {
    return Math.min(12, Math.ceil(entry.Week / 4.33));
  }
  return null;
}

/**
 * Build a sortable time key for a PeriodTransaction entry.
 * Monthly entries: "2021-05" (zero-padded month)
 * Weekly entries:  "2024-W29" (week number)
 */
function getTimeKey(entry: PeriodTransaction): string {
  if (entry.Month != null) {
    return `${entry.Year}-${String(entry.Month).padStart(2, '0')}`;
  }
  if (entry.Week != null) {
    return `${entry.Year}-W${String(entry.Week).padStart(2, '0')}`;
  }
  return `${entry.Year}`;
}


//Approximate a Date from a PeriodTransaction for range filtering.
function getApproxDate(entry: PeriodTransaction): Date {
  if (entry.Month != null) {
    return new Date(entry.Year, entry.Month - 1);
  }
  if (entry.Week != null) {
    // Approximate: week 1 starts ~Jan 1, each week adds 7 days
    const jan1 = new Date(entry.Year, 0, 1);
    const dayOffset = (entry.Week - 1) * 7;
    return new Date(jan1.getTime() + dayOffset * 86400000);
  }
  return new Date(entry.Year, 0);
}

export function prepareYearComparisonChartData(
  data: PeriodTransaction[],
  monthNames: string[]
) {
  const totals = new Map<string, number>();

  data.forEach(entry => {
    const month = resolveEntryMonth(entry);
    if (!month) return;
    const key = `${entry.Year}-${String(month).padStart(2, '0')}`;
    totals.set(key, (totals.get(key) || 0) + entry.TotalAmountPaid);
  });

  const years = Array.from(new Set(Array.from(totals.keys()).map(key => key.split('-')[0]))).sort();

  const datasets = years.map((year, index) => {
    const color = getStoreColor(index);
    const values = monthNames.map((_, monthIndex) => {
      const key = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
      return Math.round((totals.get(key) || 0) * 100) / 100;
    });

    return {
      label: year,
      data: values,
      fill: false,
      backgroundColor: color.bg,
      borderColor: color.border,
      borderWidth: 2,
      tension: 0.4,
    };
  });

  return {
    labels: monthNames,
    datasets,
  };
}

export function filterByDateRange(
  data: PeriodTransaction[],
  fromDate: Date | null,
  toDate: Date | null
): PeriodTransaction[] {
  if (!fromDate && !toDate) return data;

  return data.filter(entry => {
    const entryDate = getApproxDate(entry);
    if (fromDate && entryDate < new Date(fromDate.getFullYear(), fromDate.getMonth())) return false;
    if (toDate && entryDate > new Date(toDate.getFullYear(), toDate.getMonth())) return false;
    return true;
  });
}

export function filterByStores(
  data: PeriodTransaction[],
  storeIds: string[]
): PeriodTransaction[] {
  if (!storeIds.length) return data;
  return data.filter(entry =>
    entry.Stores.some(s => storeIds.includes(s))
  );
}

export function calculateKPIs(
  dateFilteredData: PeriodTransaction[],
  storeFilteredData: PeriodTransaction[]
): KPIData {
  // --- Totals & averages from date-filtered data ---
  const totalIncome = dateFilteredData.reduce((sum, t) => sum + t.TotalAmountPaid, 0);
  const totalSales = dateFilteredData.reduce((sum, t) => sum + t.TransactionCount, 0);

  const monthEntries = dateFilteredData.filter(t => t.Month != null);
  const weekEntries = dateFilteredData.filter(t => t.Week != null && t.Month == null);

  const distinctMonths = new Set(monthEntries.map(t => `${t.Year}-${t.Month}`)).size;
  const distinctWeeks = new Set(weekEntries.map(t => `${t.Year}-W${t.Week}`)).size;

  const totalMonthsEquiv = distinctMonths + (distinctWeeks / 4.33);
  const avgPerMonth = totalMonthsEquiv > 0 ? totalIncome / totalMonthsEquiv : 0;

  const totalWeeksEquiv = distinctWeeks + (distinctMonths * 4.33);
  const avgPerWeek = totalWeeksEquiv > 0 ? totalIncome / totalWeeksEquiv : 0;

  // Growth: how much the total rose because of the latest month (within date range)
  const monthlyMap = new Map<string, { income: number; sales: number }>();
  monthEntries.forEach(t => {
    const key = `${t.Year}-${String(t.Month).padStart(2, '0')}`;
    const existing = monthlyMap.get(key) || { income: 0, sales: 0 };
    existing.income += t.TotalAmountPaid;
    existing.sales += t.TransactionCount;
    monthlyMap.set(key, existing);
  });

  const monthKeys = Array.from(monthlyMap.keys()).sort();
  const latestMonth = monthKeys.length > 0 ? monthlyMap.get(monthKeys[monthKeys.length - 1])! : null;

  const latestMonthIncome = latestMonth?.income ?? 0;
  const latestMonthSales = latestMonth?.sales ?? 0;
  const totalBeforeThisMonth = totalIncome - latestMonthIncome;
  const thisMonthIncomeGrowth = totalBeforeThisMonth > 0
    ? (latestMonthIncome / totalBeforeThisMonth) * 100
    : 0;
  const totalSalesBeforeThisMonth = totalSales - latestMonthSales;
  const thisMonthSalesGrowth = totalSalesBeforeThisMonth > 0
    ? (latestMonthSales / totalSalesBeforeThisMonth) * 100
    : 0;

  // --- "Last month" / "last week" from store-filtered data (not affected by date range) ---
  const allMonthEntries = storeFilteredData.filter(t => t.Month != null);
  const allWeekEntries = storeFilteredData.filter(t => t.Week != null && t.Month == null);

  const allMonthlyMap = new Map<string, number>();
  allMonthEntries.forEach(t => {
    const key = `${t.Year}-${String(t.Month).padStart(2, '0')}`;
    allMonthlyMap.set(key, (allMonthlyMap.get(key) || 0) + t.TotalAmountPaid);
  });
  const allMonthKeys = Array.from(allMonthlyMap.keys()).sort();
  const currentMonthIncome = allMonthKeys.length > 1
    ? allMonthlyMap.get(allMonthKeys[allMonthKeys.length - 2])!
    : 0;

  const allWeeklyMap = new Map<string, number>();
  allWeekEntries.forEach(t => {
    const key = `${t.Year}-W${String(t.Week).padStart(2, '0')}`;
    allWeeklyMap.set(key, (allWeeklyMap.get(key) || 0) + t.TotalAmountPaid);
  });
  const allWeekKeys = Array.from(allWeeklyMap.keys()).sort();
  const currentWeekIncome = allWeekKeys.length > 1
    ? allWeeklyMap.get(allWeekKeys[allWeekKeys.length - 2])!
    : 0;

  // "vs average" compares last full period to the date-filtered average shown on the card
  const currentMonthVsAvg = avgPerMonth > 0 && currentMonthIncome > 0
    ? ((currentMonthIncome - avgPerMonth) / avgPerMonth) * 100
    : 0;
  const currentWeekVsAvg = avgPerWeek > 0 && currentWeekIncome > 0
    ? ((currentWeekIncome - avgPerWeek) / avgPerWeek) * 100
    : 0;

  // --- Avg items per transaction ---
  const totalItems = dateFilteredData.reduce((sum, t) =>
    sum + t.ProductsInPeriod.reduce((s, p) => s + p.Units, 0), 0);
  const avgItemsPerTransaction = totalSales > 0 ? totalItems / totalSales : 0;

  // Last full month avg items (from store-filtered data, second-to-last month)
  const allMonthItemsMap = new Map<string, { items: number; transactions: number }>();
  allMonthEntries.forEach(t => {
    const key = `${t.Year}-${String(t.Month).padStart(2, '0')}`;
    const existing = allMonthItemsMap.get(key) || { items: 0, transactions: 0 };
    existing.items += t.ProductsInPeriod.reduce((s, p) => s + p.Units, 0);
    existing.transactions += t.TransactionCount;
    allMonthItemsMap.set(key, existing);
  });
  const allMonthItemsKeys = Array.from(allMonthItemsMap.keys()).sort();
  const lastMonthItemsEntry = allMonthItemsKeys.length > 1
    ? allMonthItemsMap.get(allMonthItemsKeys[allMonthItemsKeys.length - 2])!
    : null;
  const lastMonthAvgItems = lastMonthItemsEntry && lastMonthItemsEntry.transactions > 0
    ? lastMonthItemsEntry.items / lastMonthItemsEntry.transactions
    : 0;
  const lastMonthAvgItemsVsAvg = avgItemsPerTransaction > 0 && lastMonthAvgItems > 0
    ? ((lastMonthAvgItems - avgItemsPerTransaction) / avgItemsPerTransaction) * 100
    : 0;

  return {
    totalIncome,
    totalSales,
    avgPerMonth,
    avgPerWeek,
    currentMonthIncome,
    currentWeekIncome,
    currentMonthVsAvg,
    currentWeekVsAvg,
    thisMonthIncomeGrowth,
    thisMonthSalesGrowth,
    todaySales: 0,
    todayAmountPaid: 0,
    todayAmountCash: 0,
    avgItemsPerTransaction,
    lastMonthAvgItems,
    lastMonthAvgItemsVsAvg,
  };
}

export function getPreviousPeriodData(
  allData: PeriodTransaction[],
  fromDate: Date | null,
  toDate: Date | null,
  storeIds: string[]
): PeriodTransaction[] {
  if (!fromDate || !toDate) return [];

  const fromMonth = fromDate.getMonth();
  const fromYear = fromDate.getFullYear();
  const toMonth = toDate.getMonth();
  const toYear = toDate.getFullYear();

  // Calculate duration in months
  const durationMonths = (toYear - fromYear) * 12 + (toMonth - fromMonth) + 1;

  // Previous period: shift back by durationMonths
  const prevToDate = new Date(fromYear, fromMonth - 1); // month before fromDate
  const prevFromDate = new Date(fromYear, fromMonth - durationMonths);

  let filtered = filterByDateRange(allData, prevFromDate, prevToDate);
  filtered = filterByStores(filtered, storeIds);
  return filtered;
}

interface TimeLabel {
  key: string;
  label: string;
}

/**
 * Build time labels from data. Handles both Month and Week entries.
 * period='month': groups by month (week entries placed in their approximate month)
 * period='quarter': groups by quarter
 */
export function getTimeLabels(
  data: PeriodTransaction[],
  monthNames: string[],
  period?: 'month' | 'quarter'
): TimeLabel[] {
  if (!data.length) return [];

  if (period === 'month') {
    // For month view: week entries get mapped to their approximate month
    const monthKeySet = new Set(data.map(t => {
      if (t.Month != null) {
        return `${t.Year}-${String(t.Month).padStart(2, '0')}`;
      }
      // Approximate week → month
      const approxMonth = Math.min(12, Math.ceil((t.Week || 1) / 4.33));
      return `${t.Year}-${String(approxMonth).padStart(2, '0')}`;
    }));
    const monthKeys = Array.from(monthKeySet).sort();

    return monthKeys.map(mk => {
      const [year, month] = mk.split('-').map(Number);
      return { key: mk, label: `${monthNames[month - 1]} ${year}` };
    });
  }

  // Quarter grouping
  const quarters = new Map<string, string>();
  data.forEach(t => {
    let month = t.Month;
    if (month == null && t.Week != null) {
      month = Math.min(12, Math.ceil(t.Week / 4.33));
    }
    if (month == null) month = 1;
    const q = Math.ceil(month / 3);
    const qKey = `${t.Year}-Q${q}`;
    if (!quarters.has(qKey)) {
      quarters.set(qKey, `Q${q} ${t.Year}`);
    }
  });

  return Array.from(quarters.entries()).map(([key, label]) => ({ key, label }));
}

/**
 * Get the grouping key for a transaction entry given a period.
 */
export function getGroupKey(t: PeriodTransaction, period?: 'month' | 'week' | 'quarter'): string {
  if (period === 'week') {
    return getTimeKey(t);
  }
  if (period === 'month') {
    if (t.Month != null) {
      return `${t.Year}-${String(t.Month).padStart(2, '0')}`;
    }
    const approxMonth = Math.min(12, Math.ceil((t.Week || 1) / 4.33));
    return `${t.Year}-${String(approxMonth).padStart(2, '0')}`;
  }
  // quarter
  let month = t.Month;
  if (month == null && t.Week != null) {
    month = Math.min(12, Math.ceil(t.Week / 4.33));
  }
  if (month == null) month = 1;
  const q = Math.ceil(month / 3);
  return `${t.Year}-Q${q}`;
}

export function prepareRevenueChartData(
  data: PeriodTransaction[],
  monthNames: string[],
  period: 'month' | 'quarter',
  revenueLabel: string = 'Revenue (€)'
) {
  const labels = getTimeLabels(data, monthNames, period);

  const grouped = new Map<string, number>();
  data.forEach(t => {
    const key = getGroupKey(t, period);
    grouped.set(key, (grouped.get(key) || 0) + t.TotalAmountPaid);
  });

  const values = labels.map(l => Math.round((grouped.get(l.key) || 0) * 100) / 100);

  return {
    labels: labels.map(l => l.label),
    datasets: [
      {
        label: revenueLabel,
        data: values,
        fill: false,
        backgroundColor: 'rgba(25, 118, 210, 0.2)',
        borderColor: 'rgba(25, 118, 210, 1)',
        borderWidth: 2,
        tension: 0.4,
      },
    ],
  };
}

export function prepareComparisonChartData(
  data: PeriodTransaction[],
  storeIds: string[],
  storeNames: Map<string, string>,
  monthNames: string[],
  period: 'month' | 'quarter',
  cashLabel: string = 'Cash',
  includeCash: boolean = false
) {
  const labels = getTimeLabels(data, monthNames, period);

  const incomeDatasets: LineChartDataset[] = [];
  const salesDatasets: LineChartDataset[] = [];

  storeIds.forEach((storeId, index) => {
    const storeData = data.filter(t => t.Stores.includes(storeId));
    const color = getStoreColor(index);
    const storeName = storeNames.get(storeId) || storeId;

    const groupedIncome = new Map<string, number>();
    const groupedSales = new Map<string, number>();
    const groupedCashIncome = new Map<string, number>();
    const groupedCashSales = new Map<string, number>();
    storeData.forEach(t => {
      const key = getGroupKey(t, period);
      groupedIncome.set(key, (groupedIncome.get(key) || 0) + t.TotalAmountPaid);
      groupedSales.set(key, (groupedSales.get(key) || 0) + t.TransactionCount);
      groupedCashIncome.set(key, (groupedCashIncome.get(key) || 0) + (t.TotalAmountCash ?? 0));
      groupedCashSales.set(key, (groupedCashSales.get(key) || 0) + (t.CashTransactionCount ?? 0));
    });

    const base = { label: storeName, fill: false, backgroundColor: color.bg, borderColor: color.border, borderWidth: 2, tension: 0.4 };
    const cashBase = { ...base, label: `${storeName} (${cashLabel})`, borderDash: [6, 4] };
    incomeDatasets.push({ ...base, data: labels.map(l => Math.round((groupedIncome.get(l.key) || 0) * 100) / 100) });
    if (includeCash) incomeDatasets.push({ ...cashBase, data: labels.map(l => Math.round((groupedCashIncome.get(l.key) || 0) * 100) / 100) });
    salesDatasets.push({ ...base, data: labels.map(l => groupedSales.get(l.key) || 0) });
    if (includeCash) salesDatasets.push({ ...cashBase, data: labels.map(l => groupedCashSales.get(l.key) || 0) });
  });

  const labelStrings = labels.map(l => l.label);
  return {
    income: { labels: labelStrings, datasets: incomeDatasets },
    sales: { labels: labelStrings, datasets: salesDatasets },
  };
}
export function prepareItemSalesChartData(
  data: PeriodTransaction[],
  monthNames: string[],
  period: 'month' | 'quarter',
  label: string = 'Items Sold'
) {
  const labels = getTimeLabels(data, monthNames, period);

  const grouped = new Map<string, number>();
  data.forEach(t => {
    const key = getGroupKey(t, period);
    const units = t.ProductsInPeriod
      .filter(p => p.ProductCode !== null)
      .reduce((sum, p) => sum + p.Units, 0);
    grouped.set(key, (grouped.get(key) || 0) + units);
  });

  const values = labels.map(l => grouped.get(l.key) || 0);

  return {
    labels: labels.map(l => l.label),
    datasets: [
      {
        label,
        data: values,
        fill: false,
        backgroundColor: 'rgba(56, 142, 60, 0.2)',
        borderColor: 'rgba(56, 142, 60, 1)',
        borderWidth: 2,
        tension: 0.4,
      },
    ],
  };
}

export function formatCurrency(value: number, locale: string = 'nl-NL'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

export function formatPercentage(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}
